import React, { useState, useRef, useEffect } from 'react';
import { BACKEND_URL } from '../../hooks/useOasisBackend';
import SettingsButton from '../components/SettingsButton';
import { useUser } from '../../hooks/useUser';
import { LoginItem } from '../../components/Login';
import { SignUpItem } from '../../components/SignupButton';
import { LogoutItem } from '../../components/LogoutButton';
import ReconnectingWebSocket from 'reconnecting-websocket';
import AgenticChatWindow from '../components/AgenticChatWindow';
import { useSearchParams } from 'react-router-dom';
import { useLoggedOutMessageCounter } from '../hooks/useLoggedOutMessageCounter';
import DomainSelector from '../components/DomainSelector';

// Add styles for the buttons
const buttonStyles = `
  .theme-button {
    padding: 0.5rem 1rem;
    border-radius: 0.375rem;
    border: 1px solid var(--rhea-card-border);
    background: var(--rhea-card-bg);
    color: var(--rhea-text-secondary);
    cursor: pointer;
    transition: background-color 0.2s;
  }
  .theme-button:hover {
    background: var(--rhea-card-hover);
  }
`;

// Custom scrollbar styles
const scrollbarStyles = `
  /* For Webkit browsers (Chrome, Safari) */
  ::-webkit-scrollbar {
    display: none;
  }

  /* For Firefox */
  * {
    scrollbar-width: none;
  }
`;

interface Message {
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: Array<{
    name: string;
    arguments: any;
    response?: any;
  }>;
  searchResults?: Array<{
    title: string;
    description: string;
    url: string;
    image_url: string;
    type: string;
    domain: string;
  }>;
}

interface WebSocketMessage {
  type: 'content' | 'tool_call' | 'tool_response' | 'done' | 'error';
  content?: string;
  conversation_id?: number;
  tool_call_id?: string;
  function_name?: string;
  external_response?: {
    type: string;
    data: {
      results: Array<{
        title: string;
        description: string;
        url: string;
        image_url: string;
        type: string;
        domain: string;
      }>;
    };
  };
  error?: string;
  data?: {
    tool_call?: {
      name: string;
      arguments: any;
    };
  };
}

const AgenticSearchPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const user = useUser();
  const loggedIn = user.state === "LoggedIn";
  const { incrementMessageCount, LimitReachedMessage, hasReachedLimit, showLimitReached, setShowLimitReached, isInitialized } = useLoggedOutMessageCounter(loggedIn);
  const ws = useRef<ReconnectingWebSocket | null>(null);
  const [searchParams] = useSearchParams();

  // Get agent type and domain from URL
  const agentType = searchParams.get('agent') || 'default';
  const domainSlug = searchParams.get('domain');
  const initialQuery = searchParams.get('q');
  
  // WebSocket connection setup
  useEffect(() => {
    let prefix = 'wss://';
    if (BACKEND_URL.includes('127.0.0.1')) {
      prefix = 'ws://';
    }
    const wsUrl = `${prefix}${BACKEND_URL.replace(/^https?:\/\//, '')}/ws/conversation/`;
    
    ws.current = new ReconnectingWebSocket(wsUrl);
    
    ws.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as WebSocketMessage;

        if (data.type === 'error' || data.error) {
          const errorMessage = data.error || 'An unknown error occurred';
          setError(errorMessage);
          setIsLoading(false);
          return;
        }

        // Store conversation ID when received
        if (data.conversation_id) {
          setConversationId(data.conversation_id);
        }

        switch (data.type) {
          case 'content':
            if (data.content) {
              setMessages(prev => {
                const lastMessage = prev[prev.length - 1];
                // If there's an existing assistant message (with or without content), update it
                if (lastMessage?.role === 'assistant') {
                  return [
                    ...prev.slice(0, -1),
                    {
                      ...lastMessage,
                      content: (lastMessage.content || '') + data.content!
                    }
                  ];
                } else {
                  // Create new assistant message if none exists
                  return [...prev, { role: 'assistant' as const, content: data.content! }];
                }
              });
            }
            break;
          case 'tool_call':
            if (data.data?.tool_call) {
              const toolCall = data.data.tool_call;
              console.log("Tool call received:", toolCall.name);
              setMessages(prev => {
                const lastMessage = prev[prev.length - 1];
                if (lastMessage.role === 'assistant') {
                  const newToolCall = {
                    name: toolCall.name,
                    arguments: toolCall.arguments,
                    response: undefined
                  };
                  return [
                    ...prev.slice(0, -1),
                    {
                      ...lastMessage,
                      toolCalls: [...(lastMessage.toolCalls || []), newToolCall]
                    }
                  ];
                }
                return prev;
              });
            }
            break;
          case 'tool_response':
            if (data.external_response) {
              // Handle search results
              if (data.external_response.type === 'search_results') {
                console.log('Search results received:', {
                  function_name: data.function_name,
                  results_count: data.external_response.data.results.length,
                  results: data.external_response.data.results
                });
                
                setMessages(prev => {
                  const lastMessage = prev[prev.length - 1];
                  // If there's no assistant message or the last message is from the user,
                  // create a new assistant message with the search results
                  if (!lastMessage || lastMessage.role === 'user') {
                    return [...prev, {
                      role: 'assistant',
                      content: '',
                      searchResults: data.external_response?.data.results
                    }];
                  }
                  // Otherwise update the existing assistant message
                  return [
                    ...prev.slice(0, -1),
                    {
                      ...lastMessage,
                      searchResults: data.external_response?.data.results
                    }
                  ];
                });
              }
              
              setMessages(prev => {
                const lastMessage = prev[prev.length - 1];
                if (lastMessage.role === 'assistant' && lastMessage.toolCalls) {
                  const updatedToolCalls = lastMessage.toolCalls.map(tc => ({
                    ...tc,
                    response: tc.name === data.function_name ? data.external_response : tc.response
                  }));
                  return [
                    ...prev.slice(0, -1),
                    {
                      ...lastMessage,
                      toolCalls: updatedToolCalls
                    }
                  ];
                }
                return prev;
              });
            }
            break;
          case 'done':
            setIsLoading(false);
            break;
        }
      } catch (err) {
        console.error('Error processing WebSocket message:', err);
        setError('Error processing server response');
        setIsLoading(false);
      }
    };

    ws.current.onopen = () => {
      console.log("WebSocket connection established");
      setError(null);
      
      // Send initial query if present
      // if (initialQuery && !messages.length) {
      //   handleSendMessage(initialQuery);
      // }
    };

    ws.current.onerror = (error) => {
      console.error("WebSocket error:", error);
      setError("reconnecting...");
      setIsLoading(false);
    };

    ws.current.onclose = () => {
      console.log("WebSocket connection closed");
      setIsLoading(false);
    };

    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, []);

  const handleSendMessage = (message: string) => {
    if (!message.trim() || !ws.current) return;

    if (!loggedIn && hasReachedLimit) {
      setShowLimitReached(true);
      return;
    }

    if (!loggedIn) {
      incrementMessageCount();
    }

    setMessages(prev => [...prev, { role: 'user', content: message }]);
    setIsLoading(true);
    setError(null);

    try {
      const messageData: any = {
        message,
        model: 'gpt-4o',
        conversation_id: conversationId,
        agent_slug: agentType
      };

      // Add domain parameters if present
      if (domainSlug) {
        messageData.agent_params = {
          domain_slug: domainSlug
        };
      }

      ws.current.send(JSON.stringify(messageData));
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{buttonStyles}</style>
      <style>{scrollbarStyles}</style>
      <div className="h-screen flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--rhea-bg-primary)', color: 'var(--rhea-text-primary)' }}>
        <LimitReachedMessage />
        {/* Header */}
        <div className="flex-none bg-inherit border-b" style={{ borderColor: 'var(--rhea-card-border)' }}>
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.location.href = '/'}>
                <img src="/assets/rhea/logo.png" alt="Rhea" className="h-8" />
                <span className="text-xl font-semibold" style={{ color: 'var(--rhea-text-primary)' }}>Rhea</span>
                <span className="text-xl" style={{ color: 'var(--rhea-text-accent)' }}>
                  | {agentType === 'rhea_domain' ? '' : 'Agentic Search'}
                  {domainSlug && `${domainSlug}`}
                </span>
              </div>
              <div className="flex-1" />
              <div className="ml-4 flex items-center gap-2">
                {!loggedIn ? (
                  <>
                    <LoginItem 
                      title="Login to Rhea"
                      className="theme-button"
                    />
                    <SignUpItem 
                      title="Sign up for Rhea"
                      className="theme-button"
                    />
                  </>
                ) : (
                  <LogoutItem className="theme-button" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 container mx-auto px-2 py-2 overflow-hidden relative">
          {error && (
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg shadow-lg">
              <p className="font-semibold">Error:</p>
              <p>{error}</p>
            </div>
          )}
          <div className="bg-inherit rounded-lg h-full">
            {agentType === 'rhea_domain' && !domainSlug ? (
              <DomainSelector />
            ) : (
              <AgenticChatWindow
                messages={messages}
                isLoading={isLoading}
                onSendMessage={handleSendMessage}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AgenticSearchPage; 